import {Paginated, SelectOption} from '@chekin/core';
import {SearchableSelect, SearchableSelectProps} from '@chekin/guestapp-ui-kit';
import {useSearchableSelectQuery} from 'hooks';

type AirbnbPaginatedSearchableSelectProps<
  T = undefined,
  V extends string | number = string,
  L = string,
> = Omit<
  SearchableSelectProps<T, V, L>,
  | 'options'
  | 'searchValue'
  | 'onSearchChange'
  | 'loading'
  | 'hasNextPage'
  | 'onLoadMore'
  | 'filterOption'
> & {
  queryKey: readonly unknown[];
  queryFn: (params: {
    page: number;
    searchQuery: string;
  }) => Promise<Paginated<SelectOption<T, V, L>>>;
  resetKey?: unknown;
  minSearchQueryLength?: number;
  emptyMessage?: string;
  searchHintMessage?: string;
};

function PaginatedSearchableSelect<
  T = undefined,
  V extends string | number = string,
  L = string,
>({
  queryKey,
  queryFn,
  resetKey,
  minSearchQueryLength = 0,
  emptyMessage,
  searchHintMessage,
  value,
  ...props
}: AirbnbPaginatedSearchableSelectProps<T, V, L>) {
  const {
    options,
    searchValue,
    setSearchValue,
    onOpenChange,
    loading,
    hasNextPage,
    onLoadMore,
    canSearch,
  } = useSearchableSelectQuery({
    queryKey,
    queryFn,
    value,
    resetKey,
    minSearchQueryLength,
  });

  return (
    <SearchableSelect
      {...props}
      value={value}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onOpenChange={onOpenChange}
      loading={loading}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
      filterOption={null}
      noOptionsMessage={() => {
        if (!canSearch) {
          return (
            searchHintMessage ??
            `Type at least ${minSearchQueryLength} characters to search`
          );
        }

        return emptyMessage ?? props.noOptionsMessage?.();
      }}
    />
  );
}

export {PaginatedSearchableSelect};
export type {AirbnbPaginatedSearchableSelectProps};
